import { type TFunction } from "i18next";
import { z } from "zod";

export const getCrewFormSchema = (t: TFunction) => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const requiredMsg = (label: string) => t("form-validation.mandatory", { label });
  const requiredStringMax = (max: number, label: string) =>
    z
      .string({ required_error: requiredMsg(label) })
      .min(1, requiredMsg(label))
      .max(max, t("form-validation.max-length", { label, max }));

  return z.object({
    familyName: requiredStringMax(50, t("vessel-call.create.table-headers.family-name")),
    givenName: requiredStringMax(50, t("vessel-call.create.table-headers.given-name")),
    rank: z.string().min(
      1,
      t("form-validation.mandatory", {
        label: t("vessel-call.create.table-headers.rank"),
      }),
    ),
    nationality: requiredStringMax(50, t("vessel-call.create.table-headers.nationality")),
    dateOfBirth: z.union([
      z
        .date({
          required_error: requiredMsg(t("vessel-call.create.table-headers.date-of-birth")),
          invalid_type_error: requiredMsg(t("vessel-call.create.table-headers.date-of-birth")),
        })
        .refine(
          (d) => {
            const normalizedDate = new Date(d);
            normalizedDate.setHours(0, 0, 0, 0);
            return normalizedDate.getTime() < startOfToday.getTime();
          },
          {
            message: t("form-validation.must-be-in-past", {
              label: t("vessel-call.create.table-headers.date-of-birth"),
            }),
          },
        ),
      z.string().min(1, requiredMsg(t("vessel-call.create.table-headers.date-of-birth"))),
    ]),
    placeOfBirth: requiredStringMax(50, t("vessel-call.create.table-headers.place-of-birth")),
    gender: z
      .string({
        required_error: requiredMsg(t("vessel-call.create.table-headers.gender")),
      })
      .min(1, requiredMsg(t("vessel-call.create.table-headers.gender"))),
    passportNumber: requiredStringMax(30, t("vessel-call.create.table-headers.passport-number")),
    issuingState: requiredStringMax(
      50,
      t("vessel-call.create.table-headers.passport-issuing-country"),
    ),
    expiryDate: z.union([
      z
        .date({
          required_error: requiredMsg(t("vessel-call.create.table-headers.passport-expiry-date")),
          invalid_type_error: requiredMsg(
            t("vessel-call.create.table-headers.passport-expiry-date"),
          ),
        })
        .refine((d) => d.getTime() > Date.now(), {
          message: t("form-validation.must-be-in-future", {
            label: t("vessel-call.create.table-headers.passport-expiry-date"),
          }),
        }),
      z.string().min(1, requiredMsg(t("vessel-call.create.table-headers.passport-expiry-date"))),
    ]),
  });
};

export type CrewFormSchema = z.infer<ReturnType<typeof getCrewFormSchema>>;
